import { useSelector } from '@xstate/react'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { IoArrowForwardSharp, IoChevronDownSharp, IoChevronUpSharp } from 'react-icons/io5'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import { ProductDropFilter } from 'types/graphql'
import { ShoppingBagIcon } from 'icons/ShoppingBag'
import { media, phoneMaxWidth } from 'utils/media'
import { numberToCurrency } from 'utils/numberToCurrency'
import { Button, LinkButton } from 'lib/Button/Button'
import { FloatingPopover, useFloatingPopoverController } from 'lib/FloatingPopover'
import { Text } from 'lib/Text'
import { useCartContext } from 'modules/cart/context/CartProvider'
import { LiveDot } from 'modules/thingdrops/components/LiveDot'
import { useProductDrops } from 'modules/thingdrops/queries/useProductDrops'
import { CartPopoverItem } from './components/CartPopoverItem'

const BLACK_LIST_URLS = ['/admin', '/brandmin', '/checkout']

const Container = styled.div`
  position: fixed;
  left: 20px;
  right: 20px;
  bottom: 20px;
  background: white;
  border: 1px solid black;
  padding: 16px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
  display: flex;
  flex-direction: column;
  gap: 18px;
  z-index: 100;

  ${media.phoneUp} {
    right: 32px;
    bottom: 32px;
    left: initial;
    width: 340px;
  }
`

const AlwaysVisible = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HiddenWhenClosed = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? 'block' : 'none')};
  flex-direction: column;
`

const StyledText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CartRow = styled.div`
  display: flex;
  padding: 8px 0;
  border-top: 1px solid var(--neutral-20);
  justify-content: space-between;

  &:last-of-type {
    margin-bottom: 16px;
  }
`

const MoreDropsButton = styled(LinkButton)`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const TextAndIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const CartPopover = () => {
  const [open, setOpen] = useState(false)
  const controls = useFloatingPopoverController()
  const isMobile = useMedia(`(max-width: ${phoneMaxWidth}px)`, false)
  const { pathname, events: routerEvents } = useRouter()
  const { cartService } = useCartContext()
  const cart = useSelector(cartService, (state) => state.context.cart)

  const cartItemsCount = cart?.bags.reduce((total, bag) => bag.skus.length + total, 0) ?? 0

  const shouldBeHidden = useMemo(() => {
    return BLACK_LIST_URLS.some((url) => pathname.startsWith(url))
  }, [pathname])

  const skus = useMemo(() => cart?.bags.flatMap((bag) => bag.skus.filter((x) => !!x)), [cart])

  const { data: productDropsData } = useProductDrops({ variables: { filter: ProductDropFilter.Visible, isPromoted: true } })

  const showMoreDropsButton = useMemo(() => {
    if (!productDropsData?.productDrops) {
      return false
    }

    return productDropsData.productDrops.filter((drop) => drop.hasDropped).length > 1
  }, [productDropsData?.productDrops])

  useEffect(() => {
    const handleRouteChange = () => {
      if (open) {
        setOpen(false)
      }
    }

    routerEvents.on('routeChangeStart', handleRouteChange)

    return () => {
      routerEvents.off('routeChangeStart', handleRouteChange)
    }
  }, [routerEvents, open])

  useEffect(() => {
    setOpen(true)
  }, [cartItemsCount])

  const content = useMemo(
    () =>
      cart &&
      skus && (
        <ContentContainer>
          {skus.map((sku) => (
            <CartRow key={sku.id}>
              <CartPopoverItem currency={cart.currency ?? 'USD'} sku={sku} />
            </CartRow>
          ))}

          <CartRow>
            <Text weight="medium" size="ps">
              Subtotal
            </Text>
            <Text weight="medium" size="ps">
              {cart.subTotal && numberToCurrency(cart.currency ?? 'USD', cart.subTotal)}
            </Text>
          </CartRow>

          <LinkButton href={`/checkout/${cart.id}`} color="primary">
            Checkout
          </LinkButton>
          {showMoreDropsButton && (
            <MoreDropsButton href="/thingdrop">
              <>
                <TextAndIconContainer>
                  <LiveDot />
                  <Text size="p" weight="medium" color="red-60">
                    LIVE!
                  </Text>
                </TextAndIconContainer>
                <TextAndIconContainer>
                  <Text size="p" weight="medium">
                    More drops
                  </Text>
                  <IoArrowForwardSharp />
                </TextAndIconContainer>
              </>
            </MoreDropsButton>
          )}
        </ContentContainer>
      ),
    [skus, cart, showMoreDropsButton]
  )

  if (!cart || shouldBeHidden || cart.bags.length === 0) {
    return null
  }

  return (
    <FloatingPopover content={content} controls={controls} hideOnDesktop withHover={false}>
      <Container>
        <AlwaysVisible>
          <StyledText size="p" weight="medium">
            <ShoppingBagIcon /> Bag - {cartItemsCount}
          </StyledText>
          <Button
            label={open ? 'Hide' : 'Show bag'}
            endIcon={open ? <IoChevronDownSharp /> : <IoChevronUpSharp />}
            onClick={() => (isMobile ? controls.setOpen(true) : setOpen((x) => !x))}
            color="primary"
            variant={open ? 'ghost' : 'normal'}
          />
        </AlwaysVisible>
        <HiddenWhenClosed $visible={open}>{content}</HiddenWhenClosed>
      </Container>
    </FloatingPopover>
  )
}
